import React from 'react';
import BaseFactory from './BaseFactory';
import {
  getCityAnchorTag,
  getFaqsAnchorTag,
  getBlogAnchorTag,
  getNextOpenDayInfo,
  hasLocationHours,
  hasBrandName,
  getNearbySolvPartner,
  getBrandName,
  getNumLocationsWithinBrand,
  getClinicAnchorTag,
  getNearbyBrandLocation,
  getLocationDistanceInMiles,
  getNearbyClinicDistanceStr,
  NoTopMarginParagraph,
  NoBottomMarginParagraph,
  hasCustomDescription,
} from './util';
import { isEmptyObject } from '../../../core/util/object';
import CustomDescriptionSection from './CustomDescriptionSection';
import { SOLV_SECTION_TITLE } from './constants';

class NonPartnerUrgentCareV1 extends BaseFactory {
  constructor(location, nearbyLocations, properties) {
    super(location, properties);
    this.clinicsToChooseFrom = [];

    // nearby solv location
    if (!isEmptyObject(nearbyLocations)) {
      this.nearbyLocations = nearbyLocations;
      this.nearbyClinics = nearbyLocations.nearby;
      const nearbySolvPartner = getNearbySolvPartner(this.nearbyClinics);

      // general nearby locations (solv partner or not)
      this.clinicsToChooseFrom = [...this.nearbyClinics];
      if (!isEmptyObject(nearbySolvPartner)) {
        this.clinicsToChooseFrom.shift();
      }

      if (this.clinicsToChooseFrom.length >= 2) {
        const [nearbyClinic1, nearbyClinic2] = this.clinicsToChooseFrom;
        this.nearbyClinic1AnchorTag = getClinicAnchorTag(nearbyClinic1, nearbyClinic1.name);
        this.nearbyClinic2AnchorTag = getClinicAnchorTag(nearbyClinic2, nearbyClinic2.name);
        this.nearbyClinicDistanceStr = getNearbyClinicDistanceStr(nearbyClinic1, nearbyClinic2);
      }

      // nearby brand location
      const nearbyBrandClinic = getNearbyBrandLocation(nearbyLocations);
      this.nearbyBrandClinic = nearbyBrandClinic;
      if (!isEmptyObject(nearbyBrandClinic)) {
        this.brandName = getBrandName(nearbyBrandClinic);
        this.numAffiliates = getNumLocationsWithinBrand(nearbyLocations.brand) - 1;
        this.nearbyBrandClinicAnchorTag = getClinicAnchorTag(nearbyBrandClinic, 'clinic');
        this.nearbyBrandClinicCityAnchorTag = getCityAnchorTag(nearbyBrandClinic);
        this.nearbyBrandClinicDistance = getLocationDistanceInMiles(nearbyBrandClinic);

        const nearbyBrandClinicNextOpenDayInfo = getNextOpenDayInfo(nearbyBrandClinic);
        this.nearbyBrandClinicNextOpenDayName = nearbyBrandClinicNextOpenDayInfo.name;
        this.nearbyBrandClinicNextOpenDayHoursStr =
          nearbyBrandClinicNextOpenDayInfo.openHoursString;
      }
    }
  }

  getDescription() {
    return (
      `${this.clinicName} is a ${this.clinicalCategory} located ${this.address}, ${this.city}, ` +
      `${this.state}, ${this.zip} providing immediate, non-life-threatening healthcare` +
      `services to the ${this.city} area. For more information, call ${this.clinicName}` +
      `${this.phoneString}.`
    );
  }

  getKeywords() {
    return (
      `${this.clinicName}, ${this.clinicName}, ${this.city}, ${this.clinicName} ${this.zip}, ` +
      `urgent care ${this.city}, urgent care ${this.zip}, ` +
      `walk-in clinic ${this.city}, walk-in clinic ${this.zip}, ${this.city} ${this.state} ` +
      `clinic, ${this.zip} clinic`
    );
  }

  getBody() {
    return (
      <span>
        <CustomDescriptionSection location={this.location} />
        <NoTopMarginParagraph>
          {hasCustomDescription(this.location) && (
            <span>
              {SOLV_SECTION_TITLE}
              <br />
            </span>
          )}
          Serving both adult and pediatric patients in the {this.cityAnchorTag} area,{' '}
          {this.clinicName} sees walk-in patients{' '}
          {hasLocationHours(this.location) && <span>{this.numDaysOpenString} a week, </span>}
          treating non-life threatening injuries and illnesses, as well as routine immunizations and
          well visits. You can find this clinic located at {this.fullAddress}
          {hasLocationHours(this.location) && (
            <span>
              {' '}
              and patients can walk in to see them {this.nextOpenDayName} from{' '}
              {this.nextOpenDayHoursStr}
            </span>
          )}
          .
        </NoTopMarginParagraph>
        {hasBrandName(this.nearbyLocations) && (
          <p>
            As part of the larger {this.brandName} group, {this.clinicName} has {this.numAffiliates}{' '}
            affiliates with the closest other {this.nearbyBrandClinicAnchorTag} being in{' '}
            {this.nearbyBrandClinicCityAnchorTag} about {this.nearbyBrandClinicDistance} miles away
            {hasLocationHours(this.nearbyBrandClinic) && (
              <span>
                {' '}
                and open {this.nearbyBrandClinicNextOpenDayName} from{' '}
                {this.nearbyBrandClinicNextOpenDayHoursStr}
              </span>
            )}
            .
            {this.clinicsToChooseFrom.length >= 2 && (
              <span>
                {' '}
                Other nearby clinics include {this.nearbyClinic1AnchorTag} and{' '}
                {this.nearbyClinic2AnchorTag}
                {this.nearbyClinicDistanceStr}.
              </span>
            )}
          </p>
        )}
        <NoBottomMarginParagraph>
          If you are looking to learn more about urgent care including statistics, trends,{' '}
          {getFaqsAnchorTag()} and other useful information, check out our {getBlogAnchorTag()} to
          empower your knowledge as a healthcare consumer. {this.cityGuideContent}
        </NoBottomMarginParagraph>
      </span>
    );
  }
}

export default NonPartnerUrgentCareV1;
